import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tabs,
  Tab,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CampaignIcon from '@mui/icons-material/Campaign';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import api from '../../utils/api';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function Communication() {
  const [tab, setTab] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    recipient_type: 'select_members',
    member_ids: [],
    level_data_id: '',
    group_id: '',
    position_id: '',
    message: '',
  });
  const [announcementData, setAnnouncementData] = useState({
    title: '',
    message: '',
    recipient_type: 'all_members',
    group_id: '',
    member_ids: [],
    also_send_email: false,
  });
  const [emailData, setEmailData] = useState({
    title: '',
    recipient_type: 'select_members',
    member_ids: [],
    level_data_id: '',
    group_id: '',
    position_id: '',
    message: '',
  });
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [positions, setPositions] = useState([]);
  const [constituencies, setConstituencies] = useState([]);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [smsConfigured, setSmsConfigured] = useState(false);
  const [smsProvider, setSmsProvider] = useState('');

  useEffect(() => {
    fetchStats();
    fetchMembers();
    fetchGroups();
    fetchPositions();
    fetchConstituencies();
    fetchEmailStatus();
    fetchSmsStatus();
  }, []);

  const fetchSmsStatus = async () => {
    try {
      const res = await api.get('/communication/sms-status');
      setSmsConfigured(res.data?.configured ?? false);
      setSmsProvider(res.data?.provider || '');
    } catch {
      setSmsConfigured(false);
    }
  };

  const fetchEmailStatus = async () => {
    try {
      const res = await api.get('/communication/email-status');
      setEmailConfigured(res.data?.configured ?? false);
    } catch {
      setEmailConfigured(false);
    }
  };

  const fetchConstituencies = async () => {
    try {
      const res = await api.get('/communication/constituencies');
      setConstituencies(res.data?.data || []);
    } catch {
      setConstituencies([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/communication/stats');
      setStats(response.data.data);
    } catch (err) {
      setError('Failed to fetch SMS statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await api.get('/members', { params: { limit: 1000, status: 'Active' } });
      setMembers(response.data.data || []);
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/hr/groups', { params: { limit: 1000 } });
      setGroups(response.data.data || []);
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await api.get('/hr/positions', { params: { limit: 1000, active: true } });
      setPositions(response.data.data || []);
    } catch (err) {
      console.error('Error fetching positions:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSending(true);

    try {
      // Validate required fields
      if (!formData.title || !formData.recipient_type || !formData.message) {
        setError('Title, Recipient Type, and Message are required');
        setSending(false);
        return;
      }

      // Validate recipient-specific fields
      if (formData.recipient_type === 'select_members' && (!formData.member_ids || formData.member_ids.length === 0)) {
        setError('Please select at least one member');
        setSending(false);
        return;
      }
      if (formData.recipient_type === 'level' && !formData.level_data_id) {
        setError('Please select a level');
        setSending(false);
        return;
      }
      if (formData.recipient_type === 'group' && !formData.group_id) {
        setError('Please select a group');
        setSending(false);
        return;
      }
      if (formData.recipient_type === 'position' && !formData.position_id) {
        setError('Please select a position');
        setSending(false);
        return;
      }

      const data = {
        title: formData.title.trim(),
        recipient_type: formData.recipient_type,
        message: formData.message.trim(),
      };

      // Add recipient-specific data
      if (formData.recipient_type === 'select_members') {
        data.member_ids = formData.member_ids;
      } else if (formData.recipient_type === 'level') {
        data.level_data_id = formData.level_data_id;
      } else if (formData.recipient_type === 'group') {
        data.group_id = formData.group_id;
      } else if (formData.recipient_type === 'position') {
        data.position_id = formData.position_id;
      }

      const response = await api.post('/communication/send-sms', data);
      setSuccess(`SMS sent successfully! Sent to ${response.data.sent_count} recipients. ${response.data.sms_used} SMS used.`);
      setFormData({
        title: '',
        recipient_type: 'select_members',
        member_ids: [],
        level_data_id: '',
        group_id: '',
        position_id: '',
        message: '',
      });
      fetchStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const handleMemberToggle = (memberId) => {
    setFormData({
      ...formData,
      member_ids: formData.member_ids.includes(memberId)
        ? formData.member_ids.filter((id) => id !== memberId)
        : [...formData.member_ids, memberId],
    });
  };

  const handleAnnouncementMemberToggle = (memberId) => {
    setAnnouncementData({
      ...announcementData,
      member_ids: announcementData.member_ids.includes(memberId)
        ? announcementData.member_ids.filter((id) => id !== memberId)
        : [...announcementData.member_ids, memberId],
    });
  };

  const handleEmailMemberToggle = (memberId) => {
    setEmailData({
      ...emailData,
      member_ids: emailData.member_ids.includes(memberId)
        ? emailData.member_ids.filter((id) => id !== memberId)
        : [...emailData.member_ids, memberId],
    });
  };

  const handleSubmitEmail = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSending(true);
    try {
      if (!emailData.title || !emailData.recipient_type || !emailData.message) {
        setError('Title, recipient type, and message are required');
        setSending(false);
        return;
      }
      if (emailData.recipient_type === 'select_members' && (!emailData.member_ids || emailData.member_ids.length === 0)) {
        setError('Please select at least one member');
        setSending(false);
        return;
      }
      if (emailData.recipient_type === 'level' && !emailData.level_data_id) {
        setError('Please select a constituency');
        setSending(false);
        return;
      }
      if (emailData.recipient_type === 'group' && !emailData.group_id) {
        setError('Please select a group');
        setSending(false);
        return;
      }
      if (emailData.recipient_type === 'position' && !emailData.position_id) {
        setError('Please select a position');
        setSending(false);
        return;
      }
      const payload = {
        title: emailData.title.trim(),
        recipient_type: emailData.recipient_type,
        message: emailData.message.trim(),
      };
      if (emailData.recipient_type === 'select_members') payload.member_ids = emailData.member_ids;
      if (emailData.recipient_type === 'level') payload.level_data_id = emailData.level_data_id;
      if (emailData.recipient_type === 'group') payload.group_id = emailData.group_id;
      if (emailData.recipient_type === 'position') payload.position_id = emailData.position_id;
      const response = await api.post('/communication/send-email', payload);
      setSuccess(`Email sent successfully to ${response.data.sent_count} recipients.`);
      setEmailData({
        title: '',
        recipient_type: 'select_members',
        member_ids: [],
        level_data_id: '',
        group_id: '',
        position_id: '',
        message: '',
      });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleSubmitAnnouncement = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSending(true);
    try {
      if (!announcementData.title || !announcementData.message) {
        setError('Title and message are required');
        setSending(false);
        return;
      }
      if (announcementData.recipient_type === 'group' && !announcementData.group_id) {
        setError('Please select a group');
        setSending(false);
        return;
      }
      if (announcementData.recipient_type === 'select_members' && (!announcementData.member_ids || announcementData.member_ids.length === 0)) {
        setError('Please select at least one member');
        setSending(false);
        return;
      }
      const payload = {
        title: announcementData.title.trim(),
        message: announcementData.message.trim(),
        recipient_type: announcementData.recipient_type,
        also_send_email: !!announcementData.also_send_email,
      };
      if (announcementData.recipient_type === 'group') payload.group_id = announcementData.group_id;
      if (announcementData.recipient_type === 'select_members') payload.member_ids = announcementData.member_ids;
      const response = await api.post('/communication/send-announcement', payload);
      const emailNote = response.data.email_sent ? ` ${response.data.email_sent} emails sent.` : '';
      setSuccess(`Announcement sent! Delivered to ${response.data.recipient_count} members.${emailNote} (Visible in member portal)`);
      setAnnouncementData({ title: '', message: '', recipient_type: 'all_members', group_id: '', member_ids: [], also_send_email: false });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send announcement');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Operations / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Communication</Box>
          </>
        }
        title="Communication"
        actions={
          <>
            <Button component={Link} to="/dashboard/communication/purchaseHistory" variant="outlined" size="small" sx={{ borderColor: tokens.border, color: tokens.ink }}>
              Purchase History
            </Button>
            <Button component={Link} to="/dashboard/communication/smsHistory" variant="outlined" size="small" sx={{ borderColor: tokens.border, color: tokens.ink }}>
              SMS History
            </Button>
          </>
        }
      />
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={surfaceCardSx}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                SMS Statistics
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Purchased
                </Typography>
                <Typography variant="h4">{stats?.total_purchased || 0}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Used
                </Typography>
                <Typography variant="h4">{stats?.total_used || 0}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  SMS Left
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats?.sms_left || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
              <Tab icon={<SmsIcon />} iconPosition="start" label="Send SMS" />
              <Tab icon={<EmailIcon />} iconPosition="start" label="Send Email (Gmail)" />
              <Tab icon={<CampaignIcon />} iconPosition="start" label="Send Announcement (In-App)" />
            </Tabs>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            {tab === 0 && (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                {!smsConfigured && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      SMS gateway not configured. Messages will be recorded but not delivered.
                    </Alert>
                  </Grid>
                )}
                {smsConfigured && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      SMS via {smsProvider}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Recipient Type</InputLabel>
                    <Select
                      value={formData.recipient_type}
                      onChange={(e) => setFormData({ ...formData, recipient_type: e.target.value })}
                      label="Recipient Type"
                    >
                      <MenuItem value="select_members">Select Members</MenuItem>
                      <MenuItem value="all_members">All Members</MenuItem>
                      <MenuItem value="level">By Level</MenuItem>
                      <MenuItem value="group">By Group</MenuItem>
                      <MenuItem value="position">By Position</MenuItem>
                      <MenuItem value="alumni">Alumni</MenuItem>
                      <MenuItem value="pension">Pensioners</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {formData.recipient_type === 'select_members' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Select Members ({formData.member_ids.length} selected)
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
                      {members.map((member) => (
                        <Chip
                          key={member.id}
                          label={`${member.first_name} ${member.last_name}`}
                          onClick={() => handleMemberToggle(member.id)}
                          color={formData.member_ids.includes(member.id) ? 'primary' : 'default'}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}

                {formData.recipient_type === 'group' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Group</InputLabel>
                      <Select
                        value={formData.group_id}
                        onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                        label="Group"
                      >
                        {groups.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {formData.recipient_type === 'position' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Position</InputLabel>
                      <Select
                        value={formData.position_id}
                        onChange={(e) => setFormData({ ...formData, position_id: e.target.value })}
                        label="Position"
                      >
                        {positions.map((position) => (
                          <MenuItem key={position.id} value={position.id}>
                            {position.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    multiline
                    rows={5}
                    required
                    helperText={`${formData.message.length} characters`}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SendIcon />}
                    disabled={sending || !formData.title || !formData.message}
                    fullWidth
                  >
                    {sending ? 'Sending...' : 'Send SMS'}
                  </Button>
                </Grid>
              </Grid>
            </form>
            )}

            {tab === 1 && (
            <form onSubmit={handleSubmitEmail}>
              <Grid container spacing={2}>
                {!emailConfigured && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="error.main" sx={{ mb: 1 }}>
                    Email not configured. See EMAIL_SETUP.md
                  </Typography>
                </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={emailData.title}
                    onChange={(e) => setEmailData({ ...emailData, title: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Recipient Type</InputLabel>
                    <Select
                      value={emailData.recipient_type}
                      onChange={(e) => setEmailData({ ...emailData, recipient_type: e.target.value })}
                      label="Recipient Type"
                    >
                      <MenuItem value="select_members">Select Members</MenuItem>
                      <MenuItem value="all_members">All Members</MenuItem>
                      <MenuItem value="level">By Constituency</MenuItem>
                      <MenuItem value="group">By Group</MenuItem>
                      <MenuItem value="position">By Position</MenuItem>
                      <MenuItem value="alumni">Alumni</MenuItem>
                      <MenuItem value="pension">Pensioners</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {emailData.recipient_type === 'select_members' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Select Members ({emailData.member_ids.length} selected)
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
                      {members.map((member) => (
                        <Chip
                          key={member.id}
                          label={`${member.first_name} ${member.last_name}`}
                          onClick={() => handleEmailMemberToggle(member.id)}
                          color={emailData.member_ids.includes(member.id) ? 'primary' : 'default'}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                {emailData.recipient_type === 'level' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Constituency</InputLabel>
                      <Select
                        value={emailData.level_data_id}
                        onChange={(e) => setEmailData({ ...emailData, level_data_id: e.target.value })}
                        label="Constituency"
                      >
                        {constituencies.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {emailData.recipient_type === 'group' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Group</InputLabel>
                      <Select
                        value={emailData.group_id}
                        onChange={(e) => setEmailData({ ...emailData, group_id: e.target.value })}
                        label="Group"
                      >
                        {groups.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {emailData.recipient_type === 'position' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Position</InputLabel>
                      <Select
                        value={emailData.position_id}
                        onChange={(e) => setEmailData({ ...emailData, position_id: e.target.value })}
                        label="Position"
                      >
                        {positions.map((position) => (
                          <MenuItem key={position.id} value={position.id}>
                            {position.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    value={emailData.message}
                    onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                    multiline
                    rows={5}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<EmailIcon />}
                    disabled={sending || !emailConfigured || !emailData.title || !emailData.message}
                    fullWidth
                  >
                    {sending ? 'Sending...' : 'Send Email'}
                  </Button>
                </Grid>
              </Grid>
            </form>
            )}

            {tab === 2 && (
            <form onSubmit={handleSubmitAnnouncement}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    In-app announcements appear in the member portal. Optionally send via email too.
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Title"
                    value={announcementData.title}
                    onChange={(e) => setAnnouncementData({ ...announcementData, title: e.target.value })}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Recipient Type</InputLabel>
                    <Select
                      value={announcementData.recipient_type}
                      onChange={(e) => setAnnouncementData({ ...announcementData, recipient_type: e.target.value })}
                      label="Recipient Type"
                    >
                      <MenuItem value="all_members">All Members</MenuItem>
                      <MenuItem value="group">By Group</MenuItem>
                      <MenuItem value="select_members">Select Members</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {announcementData.recipient_type === 'group' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Group</InputLabel>
                      <Select
                        value={announcementData.group_id}
                        onChange={(e) => setAnnouncementData({ ...announcementData, group_id: e.target.value })}
                        label="Group"
                      >
                        {groups.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            {group.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {announcementData.recipient_type === 'select_members' && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Select Members ({announcementData.member_ids.length} selected)
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid #ddd', p: 1, borderRadius: 1 }}>
                      {members.map((member) => (
                        <Chip
                          key={member.id}
                          label={`${member.first_name} ${member.last_name}`}
                          onClick={() => handleAnnouncementMemberToggle(member.id)}
                          color={announcementData.member_ids.includes(member.id) ? 'primary' : 'default'}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    value={announcementData.message}
                    onChange={(e) => setAnnouncementData({ ...announcementData, message: e.target.value })}
                    multiline
                    rows={5}
                    required
                  />
                </Grid>
                {emailConfigured && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={announcementData.also_send_email}
                          onChange={(e) => setAnnouncementData({ ...announcementData, also_send_email: e.target.checked })}
                        />
                      }
                      label="Also send via email (Gmail)"
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    startIcon={<CampaignIcon />}
                    disabled={sending || !announcementData.title || !announcementData.message}
                    fullWidth
                  >
                    {sending ? 'Sending...' : 'Send Announcement'}
                  </Button>
                </Grid>
              </Grid>
            </form>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
